import { getAuthHeaders, isAuthenticated } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.centroautomotrizhlfrd.com.do/api';

const redirectToLogin = () => {
  localStorage.removeItem('client-auth-storage');
  window.location.href = '/login';
};

const apiRequest = async (url: string, options: RequestInit = {}): Promise<Response> => {
  if (!isAuthenticated()) {
    redirectToLogin();
    throw new Error('No hay sesión activa');
  }

  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers
    }
  };

  const response = await fetch(url, config);

  if (response.status === 401) {
    redirectToLogin();
    throw new Error('Sesión expirada');
  }

  return response;
};

export interface Vehicle {
  id: string;
  customerId: string;
  brand: string;
  model: string;
  year: number;
  plate: string;
  color?: string;
  vin?: string;
  nickname?: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVehicleData {
  brand: string;
  model: string;
  year: number;
  plate: string;
  color?: string;
  vin?: string;
  nickname?: string;
  isDefault?: boolean;
}

export interface UpdateVehicleData {
  brand?: string;
  model?: string;
  year?: number;
  plate?: string;
  color?: string;
  vin?: string;
  nickname?: string;
  isDefault?: boolean;
}


// Función para obtener todos los vehículos del cliente
export const getVehicles = async (): Promise<Vehicle[]> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/vehicles`);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText}`);
    }

    const vehicles = await response.json();
    return vehicles;
  } catch (error) {
    console.error('Error fetching vehicles:', error);
    throw error;
  }
};

// Función para crear un nuevo vehículo
export const createVehicle = async (vehicleData: CreateVehicleData): Promise<Vehicle> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/vehicles`, {
      method: 'POST',
      body: JSON.stringify(vehicleData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const vehicle = await response.json();
    return vehicle;
  } catch (error) {
    console.error('Error creating vehicle:', error);
    throw error;
  }
};

// Función para actualizar un vehículo
export const updateVehicle = async (vehicleId: string, vehicleData: UpdateVehicleData): Promise<Vehicle> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/vehicles/${vehicleId}`, {
      method: 'PUT',
      body: JSON.stringify(vehicleData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const vehicle = await response.json();
    return vehicle;
  } catch (error) {
    console.error('Error updating vehicle:', error);
    throw error;
  }
};

// Función para eliminar un vehículo
export const deleteVehicle = async (vehicleId: string): Promise<void> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/vehicles/${vehicleId}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error deleting vehicle:', error);
    throw error;
  }
};

// Función para establecer un vehículo como predeterminado
export const setDefaultVehicle = async (vehicleId: string): Promise<void> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/vehicles/${vehicleId}/set-default`, {
      method: 'PUT'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error setting default vehicle:', error);
    throw error;
  }
};
